import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { isDefined, isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs } from '@trimble-gcs/i18n-types';
import { TransferSpeedPipe } from '@trimble-gcs/ngx-common';
import { ImportScan, ImportStatus } from '../import.models';
import { importTimeRemainingTranslations } from './import-time-remaining.translations';

@Component({
  selector: 'sd-import-time-remaining',
  standalone: true,
  template: `{{ remainingTime() }}`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportTimeRemainingComponent {
  private readonly translationService = inject(TranslationService);

  readonly importStatus = input.required<ImportStatus>();
  readonly importScans = input.required<ImportScan[]>();

  readonly remainingTime = computed(() => this.getRemainingTime());

  private readonly translations = importTimeRemainingTranslations();
  private readonly transferSpeedPipe = new TransferSpeedPipe();

  private startedTime?: number;

  constructor() {
    // Reset start time if import status changes to not busy.
    effect(() => {
      if (this.importStatus() !== ImportStatus.Busy) this.startedTime = undefined;
    });
  }

  private getRemainingTime(): string | undefined {
    if (this.importStatus() !== ImportStatus.Busy) return;

    const scans = this.importScans();
    const bytesTransferred = this.getBytesTransferred(scans);
    if (bytesTransferred === 0) return;

    this.startedTime = this.startedTime ?? this.getStartedTime(scans);
    if (isNil(this.startedTime)) return;

    const secondsFromStart = (Date.now() - this.startedTime) / 1000;
    if (secondsFromStart === 0) return;

    const totalSize = this.getTotalSize(scans);
    const bytesRemaning = totalSize - bytesTransferred;
    const transferRateBytesPerSecond = bytesTransferred / secondsFromStart;
    const secondsRemaining = bytesRemaning / transferRateBytesPerSecond;

    return this.getRemainingTimeFormatted(secondsRemaining, transferRateBytesPerSecond);
  }

  private getBytesTransferred(scans: ImportScan[]): number {
    return scans
      .flatMap((importScan) => importScan.files)
      .map((importFile) => importFile.fileUpload?.transferred ?? 0)
      .reduce((acc, cur) => acc + cur, 0);
  }

  private getStartedTime(scans: ImportScan[]): number | undefined {
    return scans
      .flatMap((importScan) => importScan.files)
      .map((importFile) => importFile.fileUpload?.startTime?.getTime())
      .filter(isDefined)
      .reduce((acc: number | undefined, cur) => {
        return isDefined(acc) && acc < cur ? acc : cur;
      }, undefined);
  }

  private getTotalSize(scans: ImportScan[]): number {
    return scans.flatMap((scan) => scan.files).reduce((acc, cur) => acc + cur.size, 0);
  }

  private getRemainingTimeFormatted(
    secondsRemaining: number,
    transferRateBytesPerSecond: number,
  ): string {
    const totalMinutesRemaining = Math.ceil(secondsRemaining / 60);
    if (totalMinutesRemaining <= 1) return this.translations.labelLessThanMinute.text;

    const hours = Math.floor(totalMinutesRemaining / 60);
    const hoursPlural: PluralArgs = { count: hours };
    const hoursTranslations = this.translations.hours;
    const hoursFormatted =
      hours > 0 ? this.translationService.translatePlural(hoursPlural, hoursTranslations) : '';

    const minutes = Math.floor(totalMinutesRemaining % 60);
    const minutesPlural: PluralArgs = { count: minutes };
    const minutesTranslations = this.translations.minutes;
    const minutesFormatted =
      minutes > 0
        ? this.translationService.translatePlural(minutesPlural, minutesTranslations)
        : '';

    const transferSpeed = this.transferSpeedPipe.transform(transferRateBytesPerSecond, 0);

    return this.translationService.translate(this.translations.label.text, {
      hours: hoursFormatted,
      minutes: minutesFormatted,
      transferSpeed,
    });
  }
}
